/**
 * Every screen has an address (doc 06 §2, STR-6): a link from a notification
 * must open the right screen in the app and in a browser. Signed-out visits go
 * to /login and come back to the path they asked for.
 */
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useEffect, type ReactNode } from 'react';

import { AuthProvider, useAuth } from './auth';
import { ToastHost } from './components/Toast';
import { setPushTapHandler } from './core/push';
import { useSplashReady } from './core/splash';
import { AccountDeletedScreen, AccountDeleteScreen } from './screens/AccountDelete';
import { LoginScreen, SignupScreen } from './screens/Auth';
import { AppointmentScreen, NewBookingScreen } from './screens/Booking';
import { ClientScreen, ClientsScreen } from './screens/Clients';
import { DayScreen, MonthScreen, WeekScreen } from './screens/Calendar';
import { ChatScreen, InboxScreen } from './screens/Messages';
import { ScheduleScreen } from './screens/Schedule';
import { ServicesScreen } from './screens/Services';
import { SettingsScreen } from './screens/Settings';
import { TimeOffScreen } from './screens/TimeOff';
import { Placeholder } from './screens/Placeholder';
import { TodayScreen } from './screens/Today';

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Placeholder titleKey="app.loading" />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

function SignedOutOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  if (loading) return <Placeholder titleKey="app.loading" />;
  if (!user) return <>{children}</>;
  // Back to where a signed-out visit was going (STR-6); only a same-site path.
  const next = params.get('next');
  return <Navigate to={next && next.startsWith('/') && !next.startsWith('//') ? next : '/today'} replace />;
}

export function AppRoutes() {
  const inside = (node: ReactNode) => <RequireAuth>{node}</RequireAuth>;
  return (
    <Routes>
      <Route path="/register" element={<SignedOutOnly><SignupScreen /></SignedOutOnly>} />
      <Route path="/login" element={<SignedOutOnly><LoginScreen /></SignedOutOnly>} />
      <Route path="/today" element={inside(<TodayScreen />)} />
      <Route path="/day/:date" element={inside(<DayScreen />)} />
      <Route path="/week/:monday" element={inside(<WeekScreen />)} />
      <Route path="/month/:yyyyMm" element={inside(<MonthScreen />)} />
      <Route path="/appointments/new" element={inside(<NewBookingScreen />)} />
      <Route path="/appointments/:id" element={inside(<AppointmentScreen />)} />
      <Route path="/messages" element={inside(<InboxScreen />)} />
      <Route path="/messages/:clientId" element={inside(<ChatScreen />)} />
      <Route path="/clients" element={inside(<ClientsScreen />)} />
      <Route path="/clients/new" element={inside(<ClientsScreen creating />)} />
      <Route path="/clients/:id" element={inside(<ClientScreen />)} />
      <Route path="/clients/:id/:edit" element={inside(<ClientScreen />)} />
      <Route path="/settings/services" element={inside(<ServicesScreen />)} />
      <Route path="/settings/services/:id" element={inside(<ServicesScreen />)} />
      <Route path="/settings/schedule" element={inside(<ScheduleScreen />)} />
      <Route path="/settings/schedule/:weekday" element={inside(<ScheduleScreen />)} />
      <Route path="/settings/time-off" element={inside(<TimeOffScreen />)} />
      <Route path="/settings/time-off/:id" element={inside(<TimeOffScreen />)} />
      <Route path="/settings" element={inside(<SettingsScreen />)} />
      <Route path="/settings/delete-account" element={inside(<AccountDeleteScreen />)} />
      {/* Unguarded: shown after the account is gone (D-89). */}
      <Route path="/account-deleted" element={<AccountDeletedScreen />} />
      <Route path="/" element={<Navigate to="/today" replace />} />
      <Route path="*" element={<Placeholder titleKey="screen.notFound" />} />
    </Routes>
  );
}

/** A tapped push opens its screen, e.g. /messages/{client_id} (doc 05 §6).
 *  Signed out, RequireAuth sends it through /login and back. */
function PushTaps() {
  const navigate = useNavigate();
  useEffect(() => setPushTapHandler((path) => navigate(path)), [navigate]);
  return null;
}

/** The first screen ends the cold-start splash (D-86). «Сегодня» waits for its
 *  data (Today.tsx); sign-in and every other screen are ready once sign-in is known. */
function FirstScreenReady() {
  const { loading } = useAuth();
  const { pathname } = useLocation();
  useSplashReady(!loading && pathname !== '/today' && pathname !== '/');
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <FirstScreenReady />
      <PushTaps />
      <AppRoutes />
      <ToastHost />
    </AuthProvider>
  );
}
